import { ReactNode } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

type IconVariant = "success" | "warning" | "info" | "neutral";

interface DialogAction {
  label: string;
  onClick: () => void;
}

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  icon?: ReactNode;
  iconVariant: IconVariant;
  title: string;
  description: string;
  primaryAction: DialogAction;
  secondaryAction?: DialogAction;
  className?: string;
  primaryButtonClassName?: string;
  secondaryButtonClassName?: string;
}

const iconVariantStyles: Record<IconVariant, string> = {
  success: "bg-[var(--bg-fill-positive)]",
  warning: "bg-[var(--bg-fill-warning)]",
  info: "bg-[var(--primary-500,rgba(255,128,19,0.11))]",
  neutral: "bg-black/[0.045]",
};

export function ConfirmDialog({
  open,
  onOpenChange,
  icon,
  iconVariant,
  title,
  description,
  primaryAction,
  secondaryAction,
  className,
  primaryButtonClassName,
  secondaryButtonClassName,
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "border-none max-w-[500px] rounded border-black/[0.09] p-6 gap-4 shadow-[0px_8px_16px_0px_rgba(0,0,0,0.09),0px_0px_36px_0px_rgba(0,0,0,0.11)]",
          className,
        )}
      >
        {icon && (
          <div
            className={cn(
              "flex items-center justify-center w-12 h-12 rounded",
              iconVariantStyles[iconVariant],
            )}
          >
            {icon}
          </div>
        )}

        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <DialogTitle className="text-[24px] font-bold leading-[38px] tracking-[-0.02em] text-black">
              {title}
            </DialogTitle>
            <DialogDescription className="text-[14px] leading-[20px] tracking-[-0.01em] text-black">
              {description}
            </DialogDescription>
          </div>

          <div className="flex gap-3">
            {secondaryAction && (
              <button
                type="button"
                onClick={secondaryAction.onClick}
                className={cn(
                  "flex-1 h-12 min-w-[160px] rounded bg-[#EBEBEB] text-base font-bold text-black transition-colors",
                  secondaryButtonClassName,
                )}
              >
                {secondaryAction.label}
              </button>
            )}
            <button
              type="button"
              onClick={primaryAction.onClick}
              className={cn(
                "flex-1 h-12 min-w-[160px] rounded bg-primary text-base font-bold text-black  transition-colors",
                primaryButtonClassName,
              )}
            >
              {primaryAction.label}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
